import {
  createValidator,
  loadProfiles,
  type DocumentProfile,
  type DocumentSummary,
  type Validator,
} from '@photovalidator/core';

import { DOCUMENT_PROFILES } from '../generated/documentProfiles.js';

/**
 * Document profiles are data files that live outside every application.
 *
 * `DOCUMENT_PROFILES` is generated from `document-profiles/*.json` by
 * `scripts/generate-profile-bundle.mjs` (a bundler cannot import files outside
 * its own root, so the JSON is mirrored into a module). The JSON files remain
 * the single source of truth: adding a document means adding a JSON file — no
 * import to update, no list to edit, no branch to add.
 */

export interface LoadedDocuments {
  profiles: DocumentProfile[];
  documents: DocumentSummary[];
  /** Profiles that failed validation; the app never offers these. */
  issues: Array<{ profile: string; issues: Array<{ path: string; message: string }> }>;
  warnings: Array<{ profile: string; warnings: Array<{ path: string; message: string }> }>;
}

export const loadDocuments = (): LoadedDocuments => {
  const { profiles, issues, warnings } = loadProfiles([...DOCUMENT_PROFILES]);
  const validator = createValidator({ profiles });

  return { profiles, documents: validator.listDocuments(), issues, warnings };
};

let cached: { documents: LoadedDocuments; validator: Validator } | null = null;

/** Lazily builds the process-wide validator instance. */
export const getDocumentRegistry = (): { documents: LoadedDocuments; validator: Validator } => {
  if (!cached) {
    const documents = loadDocuments();
    cached = { documents, validator: createValidator({ profiles: documents.profiles }) };
  }
  return cached;
};

/** Resolves a profile from a route slug, id or alias. */
export const resolveDocument = (idOrAlias: string): DocumentProfile | undefined =>
  getDocumentRegistry().validator.getDocument(idOrAlias);

export const defaultDocumentId = (fallback = 'us-visa-ds160'): string => {
  const { documents } = getDocumentRegistry();
  return documents.documents[0]?.id ?? fallback;
};

/** Route slug for a document, used by the SEO page generator and the switcher. */
export const routeForDocument = (document: DocumentSummary): string =>
  `/validator/${document.slug ?? document.id}`;
